import ajax from './ajax';

export interface Exam {
    _id: string;
    examId: string;
    title: string;
    description?: string;
    type: string;
    status: string;
    duration?: number;
    totalMarks?: number;
    passingScore?: number;
    startDateTime?: string;
    endDateTime?: string;
    questions: any[];
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateExamData {
    title: string;
    description?: string;
    type: string;
    category?: string;
    duration?: number;
    instructions?: string;
    questions?: any[];
    accessControl?: string;
    maxAttempts?: number;
    allowBackwardNavigation?: boolean;
    questionAccessMode?: string;
    proctoring?: Record<string, any>;
    grading?: Record<string, any>;
    feedback?: Record<string, any>;
    [key: string]: any;
}

export interface UpdateExamData extends Partial<CreateExamData> {
    status?: string;
}

export interface ExamListParams {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface ExamListResponse {
    exams: Exam[];
    total: number;
    page: number;
    limit: number;
}

class ExamService {
    async listExams(params: ExamListParams = {}): Promise<ExamListResponse> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
        const query = queryParams.toString();
        return ajax.get(`/api/exams${query ? `?${query}` : ''}`);
    }

    async getExam(id: string): Promise<Exam> {
        return ajax.get(`/api/exams/${id}`);
    }

    async createExam(data: CreateExamData): Promise<Exam> {
        return ajax.post('/api/exams', data);
    }

    async updateExam(id: string, data: UpdateExamData): Promise<Exam> {
        return ajax.put(`/api/exams/${id}`, data);
    }

    async deleteExam(id: string): Promise<{ message: string }> {
        return ajax.delete(`/api/exams/${id}`);
    }

    async publishExam(id: string): Promise<Exam> {
        return ajax.post(`/api/exams/${id}/publish`);
    }

    async archiveExam(id: string): Promise<Exam> {
        return ajax.post(`/api/exams/${id}/archive`);
    }

    async unarchiveExam(id: string): Promise<Exam> {
        return ajax.post(`/api/exams/${id}/unarchive`);
    }

    async addQuestions(id: string, questionIds: string[]): Promise<Exam> {
        // Backend expects { questions: [{ questionId, marks?, timeSeconds? }] }
        return ajax.post(`/api/exams/${id}/questions`, {
            questions: questionIds.map((questionId) => ({ questionId })),
        });
    }

    async removeQuestion(examId: string, questionId: string): Promise<Exam> {
        return ajax.delete(`/api/exams/${examId}/questions/${questionId}`);
    }

    async cloneExam(id: string, title?: string): Promise<Exam> {
        return ajax.post(`/api/exams/${id}/clone`, title ? { title } : {});
    }

    async getExamAnalytics(id: string): Promise<any> {
        return ajax.get(`/api/exams/${id}/analytics`);
    }

    async sendInvites(
        id: string,
        invites: Array<{ name: string; email: string; phone?: string }>,
    ): Promise<{ successful: number; failed: number; errors: Array<{ email: string; reason: string }> }> {
        return ajax.post(`/api/exams/${id}/invites`, { invites });
    }

    async uploadInvitesCSV(
        id: string,
        file: File,
    ): Promise<{ successful: number; failed: number; errors: any[]; message?: string }> {
        const formData = new FormData();
        formData.append('file', file);
        return ajax.post(`/api/exams/${id}/invites/upload`, formData);
    }

    async getInvites(
        id: string,
        params: { page?: number; limit?: number; status?: string; search?: string } = {},
    ): Promise<{ invites: any[]; total: number; page: number; limit: number }> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) queryParams.append(key, String(value));
        });
        const query = queryParams.toString();
        return ajax.get(`/api/exams/${id}/invites${query ? `?${query}` : ''}`);
    }

    async getInviteStats(id: string): Promise<{ total: number; sent: number; opened: number; accessed: number; completed: number }> {
        return ajax.get(`/api/exams/${id}/invites/stats`);
    }

    /** Downloads the exam's attempts as a CSV file. */
    async exportResultsCSV(id: string, examTitle?: string): Promise<void> {
        const csv: string = await ajax.get(`/api/exams/${id}/results/export`);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${(examTitle || 'exam').replace(/[^a-z0-9]+/gi, '-').slice(0, 50)}-results.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    async resendInvite(examId: string, inviteId: string): Promise<{ message: string }> {
        return ajax.post(`/api/exams/${examId}/invites/${inviteId}/resend`);
    }

    async deleteInvite(examId: string, inviteId: string): Promise<{ message: string }> {
        return ajax.delete(`/api/exams/${examId}/invites/${inviteId}`);
    }
}

export default new ExamService();
